import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useState, useCallback } from 'react';
import { useId, useClickOutside } from '@mantine/hooks';
import { getDefaultZIndex, useComponentDefaultProps, useMantineTheme } from '@mantine/styles';
import { getFloatingPosition } from '../Floating/get-floating-position/get-floating-position.js';
import { usePopover } from './use-popover.js';
import { PopoverContextProvider } from './Popover.context.js';
import { PopoverTarget } from './PopoverTarget/PopoverTarget.js';
import { PopoverDropdown } from './PopoverDropdown/PopoverDropdown.js';

var _excluded = ["children", "position", "offset", "onPositionChange", "positionDependencies", "opened", "transition", "transitionDuration", "exitTransitionDuration", "width", "middlewares", "withArrow", "arrowSize", "arrowOffset", "arrowRadius", "arrowPosition", "unstyled", "classNames", "styles", "closeOnClickOutside", "withinPortal", "portalProps", "closeOnEscape", "clickOutsideEvents", "trapFocus", "onClose", "onOpen", "onChange", "zIndex", "radius", "shadow", "id", "defaultOpened", "__staticSelector", "withRoles", "disabled", "returnFocus", "variant", "keepMounted"];
var defaultProps = {
  position: 'bottom',
  offset: 8,
  positionDependencies: [],
  transition: 'fade',
  transitionDuration: 150,
  exitTransitionDuration: 0,
  middlewares: {
    flip: true,
    shift: true,
    inline: false
  },
  arrowSize: 7,
  arrowOffset: 5,
  arrowRadius: 0,
  arrowPosition: 'side',
  closeOnClickOutside: true,
  withinPortal: false,
  closeOnEscape: true,
  trapFocus: false,
  withRoles: true,
  returnFocus: false,
  clickOutsideEvents: ['mousedown', 'touchstart'],
  zIndex: getDefaultZIndex('popover'),
  __staticSelector: 'Popover',
  width: 'max-content'
};
function Popover(props) {
  var _popover$floating, _popover$floating2;
  var arrowRef = useRef(null);
  var _useComponentDefaultP = useComponentDefaultProps('Popover', defaultProps, props),
    children = _useComponentDefaultP.children,
    position = _useComponentDefaultP.position,
    offset = _useComponentDefaultP.offset,
    onPositionChange = _useComponentDefaultP.onPositionChange,
    positionDependencies = _useComponentDefaultP.positionDependencies,
    opened = _useComponentDefaultP.opened,
    transition = _useComponentDefaultP.transition,
    transitionDuration = _useComponentDefaultP.transitionDuration,
    exitTransitionDuration = _useComponentDefaultP.exitTransitionDuration,
    width = _useComponentDefaultP.width,
    middlewares = _useComponentDefaultP.middlewares,
    withArrow = _useComponentDefaultP.withArrow,
    arrowSize = _useComponentDefaultP.arrowSize,
    arrowOffset = _useComponentDefaultP.arrowOffset,
    arrowRadius = _useComponentDefaultP.arrowRadius,
    arrowPosition = _useComponentDefaultP.arrowPosition,
    unstyled = _useComponentDefaultP.unstyled,
    classNames = _useComponentDefaultP.classNames,
    styles = _useComponentDefaultP.styles,
    closeOnClickOutside = _useComponentDefaultP.closeOnClickOutside,
    withinPortal = _useComponentDefaultP.withinPortal,
    portalProps = _useComponentDefaultP.portalProps,
    closeOnEscape = _useComponentDefaultP.closeOnEscape,
    clickOutsideEvents = _useComponentDefaultP.clickOutsideEvents,
    trapFocus = _useComponentDefaultP.trapFocus,
    onClose = _useComponentDefaultP.onClose,
    onOpen = _useComponentDefaultP.onOpen,
    onChange = _useComponentDefaultP.onChange,
    zIndex = _useComponentDefaultP.zIndex,
    radius = _useComponentDefaultP.radius,
    shadow = _useComponentDefaultP.shadow,
    id = _useComponentDefaultP.id,
    defaultOpened = _useComponentDefaultP.defaultOpened,
    __staticSelector = _useComponentDefaultP.__staticSelector,
    withRoles = _useComponentDefaultP.withRoles,
    disabled = _useComponentDefaultP.disabled,
    returnFocus = _useComponentDefaultP.returnFocus,
    variant = _useComponentDefaultP.variant,
    keepMounted = _useComponentDefaultP.keepMounted,
    others = _objectWithoutProperties(_useComponentDefaultP, _excluded);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    targetNode = _useState2[0],
    setTargetNode = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    dropdownNode = _useState4[0],
    setDropdownNode = _useState4[1];
  var uid = useId(id);
  var theme = useMantineTheme();
  var popover = usePopover({
    middlewares: middlewares,
    width: width,
    position: getFloatingPosition(theme.dir, position),
    offset: typeof offset === 'number' ? offset + (withArrow ? arrowSize / 2 : 0) : offset,
    arrowRef: arrowRef,
    arrowOffset: arrowOffset,
    onPositionChange: onPositionChange,
    positionDependencies: positionDependencies,
    opened: opened,
    defaultOpened: defaultOpened,
    onChange: onChange,
    onOpen: onOpen,
    onClose: onClose
  });
  useClickOutside(function () {
    return popover.opened && closeOnClickOutside && popover.onClose();
  }, clickOutsideEvents, [targetNode, dropdownNode]);
  var reference = useCallback(function (node) {
    setTargetNode(node);
    popover.floating.refs.setReference(node);
  }, [popover.floating.elements.reference]);
  var floating = useCallback(function (node) {
    setDropdownNode(node);
    popover.floating.refs.setFloating(node);
  }, [popover.floating.elements.floating]);
  return /*#__PURE__*/React__default.createElement(PopoverContextProvider, {
    value: {
      returnFocus: returnFocus,
      disabled: disabled,
      controlled: popover.controlled,
      reference: reference,
      floating: floating,
      x: popover.floating.x,
      y: popover.floating.y,
      arrowX: (_popover$floating = popover.floating) === null || _popover$floating === void 0 || (_popover$floating = _popover$floating.middlewareData) === null || _popover$floating === void 0 || (_popover$floating = _popover$floating.arrow) === null || _popover$floating === void 0 ? void 0 : _popover$floating.x,
      arrowY: (_popover$floating2 = popover.floating) === null || _popover$floating2 === void 0 || (_popover$floating2 = _popover$floating2.middlewareData) === null || _popover$floating2 === void 0 || (_popover$floating2 = _popover$floating2.arrow) === null || _popover$floating2 === void 0 ? void 0 : _popover$floating2.y,
      opened: popover.opened,
      arrowRef: arrowRef,
      transition: transition,
      transitionDuration: transitionDuration,
      exitTransitionDuration: exitTransitionDuration,
      width: width,
      withArrow: withArrow,
      arrowSize: arrowSize,
      arrowOffset: arrowOffset,
      arrowRadius: arrowRadius,
      arrowPosition: arrowPosition,
      placement: popover.floating.placement,
      trapFocus: trapFocus,
      withinPortal: withinPortal,
      portalProps: portalProps,
      zIndex: zIndex,
      radius: radius,
      shadow: shadow,
      closeOnEscape: closeOnEscape,
      onClose: popover.onClose,
      onToggle: popover.onToggle,
      getTargetId: function getTargetId() {
        return "".concat(uid, "-target");
      },
      getDropdownId: function getDropdownId() {
        return "".concat(uid, "-dropdown");
      },
      withRoles: withRoles,
      targetProps: others,
      __staticSelector: __staticSelector,
      classNames: classNames,
      styles: styles,
      unstyled: unstyled,
      variant: variant,
      keepMounted: keepMounted
    }
  }, children);
}
Popover.Target = PopoverTarget;
Popover.Dropdown = PopoverDropdown;

export { Popover };
//# sourceMappingURL=Popover.js.map
